import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaArrowRight, FaShieldHalved, FaUser, FaPhone } from "react-icons/fa6";
import { HiOutlineDevicePhoneMobile, HiOutlineKey, HiOutlineUserPlus, HiOutlineArrowRightOnRectangle, HiOutlineCheckCircle } from "react-icons/hi2";
import ForgeLogo from "../components/logo";
import CircularBadge from "../components/badge";
import { portalAuth, setPortalToken, getPortalToken, ssoAPI } from "./portalApi";
import "./LoginPage.css";

const ease = [0.22, 1, 0.36, 1];

/* NEED-001 / LOG-004: the emailed code is how everyone signs in; a
   password is an optional extra, offered when the account is made (and
   any time in Profile → Security). The sign-in page opens on whichever
   way this browser last used, so password users get their password
   manager straight away. */
const METHOD_KEY = "forge_signin_method";
const lastMethod = () => { try { return localStorage.getItem(METHOD_KEY) === "password" ? "password" : "otp"; } catch (_) { return "otp"; } };
const rememberMethod = (m) => { try { localStorage.setItem(METHOD_KEY, m); } catch (_) {} };

/* ── OTP flow ── */
function OtpFlow({ onSuccess, presetEmail = "", startAtOtp = false, onBack = null }) {
  const [step, setStep] = useState(startAtOtp ? "otp" : "email"); // "email" | "otp"
  const [email, setEmail] = useState(presetEmail);
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(startAtOtp ? 60 : 0);

  // Registration hands over straight to the code step — start its resend timer.
  useEffect(() => {
    if (!startAtOtp) return undefined;
    const t = setInterval(() => setResendCooldown((prev) => (prev <= 1 ? (clearInterval(t), 0) : prev - 1)), 1000);
    return () => clearInterval(t);
  }, [startAtOtp]);

  const handleRequestOtp = async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      setError("Email address is required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await portalAuth.requestOtp(email.trim());
      setFirstName(res.data?.firstName || "");
      setStep("otp");
      // Cooldown 60s before resend
      setResendCooldown(60);
      const t = setInterval(
        () =>
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(t);
              return 0;
            }
            return prev - 1;
          }),
        1000,
      );
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (otp.length < 6) {
      setError("Please enter the 6-digit code.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await portalAuth.verifyOtp(email.trim(), otp.trim());
      setPortalToken(res.data.token);
      if (!startAtOtp) rememberMethod("otp");
      onSuccess(res.data.member);
    } catch (err) {
      setError(err.message || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {step === "email" ? (
        <motion.form
          key="email"
          onSubmit={handleRequestOtp}
          className="lp__form"
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.3, ease }}
        >
          <div className="lp__field">
            <label className="lp__label" htmlFor="otp-email">
              Email Address
            </label>
            <div className="lp__input-wrap">
              <FaEnvelope className="lp__input-icon" />
              <input
                id="otp-email"
                type="email"
                className="lp__input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                required
                autoComplete="email"
              />
            </div>
          </div>
          {error && <p className="lp__error">{error}</p>}
          <button type="submit" className="lp__submit" disabled={loading}>
            {loading ? "Sending code…" : "Send Sign-In Code"} {!loading && <FaArrowRight />}
          </button>
          <p className="lp__otp-hint">We'll send a 6-digit code to your email. No password needed.</p>
        </motion.form>
      ) : (
        <motion.form
          key="otp"
          onSubmit={handleVerifyOtp}
          className="lp__form"
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.3, ease }}
        >
          <div className="lp__otp-sent-notice">
            <HiOutlineDevicePhoneMobile className="lp__otp-sent-icon" />
            <div>
              <p className="lp__otp-sent-title">Code sent{firstName ? ` for ${firstName}` : ""}</p>
              <p className="lp__otp-sent-sub">
                Check your inbox at <strong>{email}</strong>
              </p>
            </div>
          </div>
          <div className="lp__field">
            <label className="lp__label" htmlFor="otp-code">
              6-Digit Sign-In Code
            </label>
            <input
              id="otp-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="lp__input lp__input--otp"
              placeholder="000000"
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                setError("");
              }}
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
            />
          </div>
          {error && <p className="lp__error">{error}</p>}
          <button type="submit" className="lp__submit" disabled={loading || otp.length < 6}>
            {loading ? "Verifying…" : "Sign In"} {!loading && <FaArrowRight />}
          </button>
          <div className="lp__otp-footer">
            <button
              type="button"
              className="lp__link-btn"
              onClick={() => {
                if (onBack) { onBack(); return; }
                setStep("email");
                setOtp("");
                setError("");
              }}
            >
              ← Use a different email
            </button>
            <button type="button" className="lp__link-btn" onClick={handleRequestOtp} disabled={resendCooldown > 0 || loading}>
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
            </button>
          </div>
        </motion.form>
      )}
    </AnimatePresence>
  );
}

/* ── Password flow ── */
function PasswordFlow({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await portalAuth.loginPassword(email.trim(), password);
      setPortalToken(res.data.token);
      rememberMethod("password");
      onSuccess(res.data.member);
    } catch (err) {
      setError(err.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form onSubmit={handleLogin} className="lp__form" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, ease }}>
      <div className="lp__field">
        <label className="lp__label" htmlFor="pw-email">
          Email Address
        </label>
        <div className="lp__input-wrap">
          <FaEnvelope className="lp__input-icon" />
          <input
            id="pw-email"
            type="email"
            className="lp__input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
            }}
            required
            autoComplete="email"
          />
        </div>
      </div>
      <div className="lp__field">
        <label className="lp__label" htmlFor="pw-pass">
          Password
        </label>
        <div className="lp__input-wrap">
          <FaLock className="lp__input-icon" />
          <input
            id="pw-pass"
            type={showPw ? "text" : "password"}
            className="lp__input"
            placeholder="Your portal password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError("");
            }}
            required
            autoComplete="current-password"
          />
          <button type="button" className="lp__pw-toggle" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw((v) => !v)}>
            {showPw ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
      </div>
      {error && <p className="lp__error">{error}</p>}
      <button type="submit" className="lp__submit" disabled={loading}>
        {loading ? "Signing in…" : "Sign In"} {!loading && <FaArrowRight />}
      </button>
    </motion.form>
  );
}

/* ── After creating an account: an optional password ──
   Signed in already (by the emailed code). Saving a password here means
   the Password tab on the sign-in page works for them; skipping keeps
   them on codes, which always work. The hidden username field lets
   password managers save the email with the new password. */
function PasswordOffer({ email, onDone }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setError("Use at least 8 characters."); return; }
    if (pw !== confirm) { setError("The two passwords don't match."); return; }
    setSaving(true); setError("");
    try {
      await portalAuth.setPassword(pw);
      rememberMethod("password");
      onDone();
    } catch (err) { setError(err.message || "Couldn't save the password. Try again."); setSaving(false); }
  };

  return (
    <motion.form onSubmit={save} noValidate className="lp__form" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, ease }}>
      <input type="email" name="username" autoComplete="username" value={email} readOnly className="lp__sr-only" tabIndex={-1} aria-hidden="true" />
      <div className="lp__field">
        <label className="lp__label" htmlFor="rg-pw">Password <span className="lp__optional">(optional)</span></label>
        <div className="lp__input-wrap">
          <FaLock className="lp__input-icon" />
          <input id="rg-pw" type={show ? "text" : "password"} className="lp__input" value={pw} autoComplete="new-password" minLength={8}
            aria-describedby="rg-pw-hint" onChange={(e) => { setPw(e.target.value); setError(""); }} />
          <button type="button" className="lp__pw-toggle" aria-label={show ? "Hide password" : "Show password"} onClick={() => setShow((v) => !v)}>
            {show ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
        <p className="lp__field-hint" id="rg-pw-hint">At least 8 characters.</p>
      </div>
      <div className="lp__field">
        <label className="lp__label" htmlFor="rg-pw2">Confirm password</label>
        <div className="lp__input-wrap">
          <FaLock className="lp__input-icon" />
          <input id="rg-pw2" type={show ? "text" : "password"} className="lp__input" value={confirm} autoComplete="new-password"
            onChange={(e) => { setConfirm(e.target.value); setError(""); }} />
        </div>
      </div>
      {error && <p className="lp__error" role="alert">{error}</p>}
      <button type="submit" className="lp__submit" disabled={saving || !pw}>
        {saving ? "Saving…" : "Save Password"} {!saving && <FaArrowRight />}
      </button>
      <p className="lp__signup">
        <button type="button" className="lp__signup-link" onClick={onDone}>Skip, I'll use sign-in codes</button>
      </p>
    </motion.form>
  );
}

/* ── Create account ──
   Open registration: anyone can create an account. The portal areas that
   need checks (application, screening, medical, booking) unlock as each
   step is approved — the account itself doesn't wait on anyone. */
function RegisterFlow({ onSuccess, onJoined }) {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [sent, setSent] = useState(false);
  const [joined, setJoined] = useState(null);   // signed in: offer a password, then go on
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) { setError("Please enter your first and last name."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setError("Please enter a valid email address."); return; }
    setLoading(true);
    try {
      await portalAuth.register({ ...form, email: form.email.trim() });
      setSent(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (joined) {
    return <PasswordOffer key="reg-pw" email={form.email.trim()} onDone={() => onSuccess(joined.member)} />;
  }
  if (sent) {
    return <OtpFlow key="reg-otp" presetEmail={form.email.trim()} startAtOtp onSuccess={(member) => { setJoined({ member }); onJoined?.(form.email.trim()); }} onBack={() => setSent(false)} />;
  }

  return (
    <motion.form key="register" onSubmit={submit} className="lp__form" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.3, ease }}>
      <div className="lp__field-row">
        <div className="lp__field">
          <label className="lp__label" htmlFor="rg-first">First name</label>
          <div className="lp__input-wrap">
            <FaUser className="lp__input-icon" />
            <input id="rg-first" className="lp__input" value={form.firstName} onChange={set("firstName")} autoComplete="given-name" required />
          </div>
        </div>
        <div className="lp__field">
          <label className="lp__label" htmlFor="rg-last">Last name</label>
          <div className="lp__input-wrap">
            <FaUser className="lp__input-icon" />
            <input id="rg-last" className="lp__input" value={form.lastName} onChange={set("lastName")} autoComplete="family-name" required />
          </div>
        </div>
      </div>
      <div className="lp__field">
        <label className="lp__label" htmlFor="rg-email">Email address</label>
        <div className="lp__input-wrap">
          <FaEnvelope className="lp__input-icon" />
          <input id="rg-email" type="email" className="lp__input" value={form.email} onChange={set("email")} autoComplete="email" required />
        </div>
      </div>
      <div className="lp__field">
        <label className="lp__label" htmlFor="rg-phone">Phone <span className="lp__optional">(optional)</span></label>
        <div className="lp__input-wrap">
          <FaPhone className="lp__input-icon" />
          <input id="rg-phone" type="tel" className="lp__input" placeholder="+1 555 123 4567" value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </div>
      </div>
      {error && <p className="lp__error">{error}</p>}
      <button type="submit" className="lp__submit" disabled={loading}>
        {loading ? "Creating account…" : "Create My Account"} {!loading && <FaArrowRight />}
      </button>
      <ul className="lp__reg-points">
        <li><HiOutlineCheckCircle /> Free, and takes under a minute</li>
        {/* Was "Apply, track your progress and book — all in one place",
            which wrapped after the dash and left it dangling at the end of
            a line. No dash, nothing to strand. (UX-REVIEW LOG-006) */}
        <li><HiOutlineCheckCircle /> Apply, track your progress and book in one place</li>
        <li><HiOutlineCheckCircle /> We'll email you a code to confirm it's you, then you can add a password if you like</li>
      </ul>
    </motion.form>
  );
}

/* ── Main LoginPage ── */
export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("mode") === "register" ? "register" : "signin"); // "signin" | "register"
  const [mode, setMode] = useState(lastMethod);
  const [joinedAs, setJoinedAs] = useState("");   // just made an account: the optional password step // "otp" | "password" (LOG-004: the way this browser last used)
  const navigate = useNavigate();
  // `redirect` is a portal path (e.g. "book" or "community/connect?sso=…")
  const redirect = searchParams.get("redirect") || "dashboard";
  const target = `/portal/${redirect.replace(/^\/+/, "").replace(/^portal\//, "")}`;

  // Already signed in → go straight through (e.g. arriving from the forum)
  useEffect(() => {
    if (getPortalToken()) navigate(target, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Arriving from the forum: Makua guests can use their Makua sign-in instead (one forum for both).
  const forForum = /^\/?(portal\/)?community\/connect\?/.test(redirect);
  const [makua, setMakua] = useState(null);
  useEffect(() => {
    if (forForum) ssoAPI.other().then((r) => setMakua(r.data)).catch(() => {});
  }, [forForum]);
  const makuaHref = makua && `${makua.url}/account/continue?next=${encodeURIComponent(target)}`;

  const handleSuccess = (member) => {
    navigate(target, { replace: true, state: { member } });
  };

  return (
    <div className="lp">
      {/* ── Left panel ── */}
      <div className="lp__left">
        <div className="lp__left-bg" aria-hidden="true">
          <img src="/assets/hero.png" alt="" className="lp__left-img" />
          <div className="lp__left-overlay" />
        </div>
        <div className="lp__left-content">
          <div className="lp__left-logo">
            <ForgeLogo fill="#b85c38" width={52} height={52} />
            <div>
              <p className="lp__brand-name">FORGE</p>
              <p className="lp__brand-sub">RETREAT COLOMBIA</p>
            </div>
          </div>
          <div className="lp__left-hero">
            <h1 className="lp__hero-headline">
              One Journey.
              <br />
              <span className="lp__hero-rust">Same Path Forward.</span>
            </h1>
            <span className="lp__hero-rule" aria-hidden="true" />
            <p className="lp__hero-sub">Your member portal — track your application, respond to messages from our team, and stay connected with Forge.</p>
          </div>
          <div className="lp__left-badge">
            <CircularBadge text="ONE JOURNEY · SAME PATH FORWARD ·" colors="rgba(255,255,255,0.5)" logoSize={28} />
          </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="lp__right">
        <motion.div className="lp__card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease }}>
          <div className="lp__card-mtn" aria-hidden="true">
            <img src="/assets/mountainsForst.png" alt="" />
          </div>

          <h2 className="lp__card-title">{joinedAs ? "Your account is ready" : view === "register" ? "Create your account" : "Member Portal"}</h2>
          <p className="lp__card-sub">
            {joinedAs
              ? <>You can always sign in with a code sent to <strong>{joinedAs}</strong>. Add a password too if you'd rather use one.</>
              : view === "register"
                ? "One account for your application, your progress and your retreat booking."
                : "Sign in with the email address on your account."}
          </p>

          {/* Sign in / Create account (not once they're in) */}
          {!joinedAs && <div className="lp__view-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={view === "signin"} className={`lp__view-tab ${view === "signin" ? "lp__view-tab--active" : ""}`} onClick={() => setView("signin")}>
              <HiOutlineArrowRightOnRectangle /> Sign in
            </button>
            <button type="button" role="tab" aria-selected={view === "register"} className={`lp__view-tab ${view === "register" ? "lp__view-tab--active" : ""}`} onClick={() => setView("register")}>
              <HiOutlineUserPlus /> Create account
            </button>
          </div>}

          {view === "register" ? (
            <AnimatePresence mode="wait">
              <RegisterFlow key="register" onSuccess={handleSuccess} onJoined={setJoinedAs} />
            </AnimatePresence>
          ) : (
            <>
              {/* Mode toggle */}
              <div className="lp__mode-toggle">
                <button type="button" className={`lp__mode-btn ${mode === "otp" ? "lp__mode-btn--active" : ""}`} onClick={() => setMode("otp")}>
                  <HiOutlineDevicePhoneMobile /> Sign-In Code
                </button>
                <button type="button" className={`lp__mode-btn ${mode === "password" ? "lp__mode-btn--active" : ""}`} onClick={() => setMode("password")}>
                  <HiOutlineKey /> Password
                </button>
              </div>

              <AnimatePresence mode="wait">{mode === "otp" ? <OtpFlow key="otp" onSuccess={handleSuccess} /> : <PasswordFlow key="pw" onSuccess={handleSuccess} />}</AnimatePresence>

              {mode === "password" && (
                <p className="lp__signup" style={{ marginTop: "1rem" }}>
                  No password set?{" "}
                  <button type="button" className="lp__signup-link" onClick={() => setMode("otp")}>
                    Use sign-in code instead
                  </button>
                </p>
              )}
              {makuaHref && (
                <p className="lp__signup lp__other-site">
                  Stayed with {makua.name}?{" "}
                  <a className="lp__signup-link" href={makuaHref}>Continue with your {makua.name} sign-in</a>
                </p>
              )}
              {/* The "New to Forge? Create an account" line used to sit here
                  and went to the same place as the "Create account" tab
                  above. The tab is the one route to registration.
                  (UX-REVIEW LOG-002) */}
            </>
          )}
        </motion.div>

        <div className="lp__security">
          <FaShieldHalved aria-hidden="true" />
          <span>Your data is secure and encrypted. Parts of the portal open as each step of your application is approved.</span>
        </div>
      </div>
    </div>
  );
}
